import { useEffect, useRef, useState, type ReactNode } from 'react';
import { UploadSimple } from '@phosphor-icons/react';
import { useProBuilderApp } from '~/hooks/queries/use-pro-builder';
import { UPLOAD_DROP_OVERLAY_TEXT } from './pro-builder-copy';
import { dragLeftWindow } from '~/lib/file-drag';
import { proBuilderDropHandlers, resetDropZone, terminalDropOpensDialog } from './pro-builder-drop';

/**
 * FRONT-3078: the terminal area takes files from the computer. While one is over it an overlay says where
 * it lands (it never takes pointer events, so the terminal underneath is not shifted or blocked); a drop
 * queues the files for the BOUND app. With `eddy.ui-refactoring` on they upload without opening the dialog and
 * a file that fails says so in a toast; off, the dialog opens as before (FRONT-3223). An in-page drag (no
 * `Files` type) is ignored.
 */
export function ProBuilderDropZone({ children }: { children: ReactNode }) {
  const folder = useProBuilderApp(true).data?.folder ?? null;
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  // Window-level settlement, like the Files tab: a drag that ends anywhere resets the zone even when the
  // terminal's renderer swapped the element under the pointer and a `dragleave` went missing.
  useEffect(() => {
    const reset = () => resetDropZone(depth, setOver);
    const leave = (e: DragEvent) => {
      if (dragLeftWindow(e, { width: window.innerWidth, height: window.innerHeight })) reset();
    };
    window.addEventListener('dragend', reset, true);
    window.addEventListener('drop', reset, true);
    window.addEventListener('dragleave', leave, true);
    return () => {
      window.removeEventListener('dragend', reset, true);
      window.removeEventListener('drop', reset, true);
      window.removeEventListener('dragleave', leave, true);
    };
  }, []);
  const handlers = proBuilderDropHandlers({ folder, depth, setOver, openDialog: terminalDropOpensDialog() });
  return (
    <div data-testid="pro-builder-drop-zone" {...handlers} className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col">
      {children}
      {over && (
        <div
          data-testid="pro-builder-drop-overlay"
          aria-hidden
          className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-primary bg-background/85 text-center"
        >
          <UploadSimple size={24} className="text-primary" />
          <span className="text-base font-semibold text-foreground">{UPLOAD_DROP_OVERLAY_TEXT}</span>
        </div>
      )}
    </div>
  );
}
